import React from 'react'
// libs
import { useSelector } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client/react'
// components
import ChartView from './components/ChartView'
import { IconButton } from 'components/UI'
import LastUpdated, { LastUpdatedVariant } from 'components/UI/LastUpdated'
// icons
import RefreshIcon from '@mui/icons-material/Refresh'
// hooks
import { useChartsRefresh } from 'hooks/useChartsRefresh'
// graphql
import { CHARTS_REFRESH_MUTATION } from 'graphql/chart/mutations'
import { CHART_DATA_QUERY, CHART_QUERY } from 'graphql/chart/queries'
// store
import { isDashboardEditSelector } from 'store/reducers/dashboards'
// types
import { Widget } from 'store/reducers/dashboards'
import { IChart, IChartsRefreshInput } from 'types/graphql'
// constants
import { DEFAULT_CHART_TILE_HEIGHT, CHART_REFRESH_LOCKED_MESSAGE } from 'constants/charts'
// views
import { ChartBody, ChartHeader, ChartMessage, ChartTitle, ChartWrapper } from './views'
// utils
import { handleError } from 'utils/errors/handlers'
import { getSavedChartConfig } from 'utils/charts/config'
import { getChartErrorMessage } from 'utils/charts/errors'

interface Props {
  data: Widget
}

const Chart: React.FC<Props> = ({ data }) => {
  const settings = data.item.settings
  const chartId = settings?.chartId

  const isEdit = useSelector(isDashboardEditSelector)

  // A deleted chart comes back as CHART_NOT_FOUND
  const { data: chartData, error } = useQuery<{ chart: IChart }>(CHART_QUERY, {
    variables: { id: chartId },
    skip: !chartId
  })

  // chartData answers REFRESHING right after, and the loader stays until the notification
  const [refreshCharts] = useMutation<unknown, { input: IChartsRefreshInput }>(CHARTS_REFRESH_MUTATION, {
    refetchQueries: [{ query: CHART_DATA_QUERY, variables: { chartId } }]
  })

  const chart = chartData?.chart

  const { refreshableIds, computedAgo } = useChartsRefresh(chartId ? [chartId] : [])

  const isRefreshLocked = !refreshableIds.length

  const config = React.useMemo(() => chart && getSavedChartConfig(chart), [chart])

  const onRefreshClick = () =>
    refreshCharts({ variables: { input: { chartIds: [chartId] } } }).catch((err) =>
      handleError(err, 'Chart.onRefreshClick')
    )

  const getMessage = () => {
    if (!chartId) {
      return 'Set up the chart in the widget settings'
    }
    if (error) {
      return getChartErrorMessage(error)
    }
    return null
  }

  const message = getMessage()

  return (
    <ChartWrapper height={settings?.height || DEFAULT_CHART_TILE_HEIGHT}>
      <ChartHeader>
        {settings?.showTitle !== false && <ChartTitle>{chart?.title || '< chart >'}</ChartTitle>}
        {config && !isEdit && computedAgo && (
          <LastUpdated value={computedAgo} variant={LastUpdatedVariant.Light} data-testid='chart-updated' />
        )}
        {config && !isEdit && (
          <IconButton
            preset='GHOST'
            size={32}
            data-testid='chart-refresh'
            description={isRefreshLocked ? CHART_REFRESH_LOCKED_MESSAGE : 'Refresh'}
            disabled={isRefreshLocked}
            onClick={onRefreshClick}>
            <RefreshIcon />
          </IconButton>
        )}
      </ChartHeader>
      {message ? (
        <ChartBody>
          <ChartMessage>{message}</ChartMessage>
        </ChartBody>
      ) : (
        config && <ChartView config={config} title={chart.title} chartId={chartId} />
      )}
    </ChartWrapper>
  )
}

export default Chart
